import type { FC } from 'react'
import { useCallback, useMemo } from 'react'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import BrightnessAuto from '@mui/icons-material/BrightnessAuto'
import LightModeIcon from '@mui/icons-material/LightMode'
import ModeNightIcon from '@mui/icons-material/ModeNight'
import useTranslate from '@/hooks/use-translate'
import { useSettings } from '@/hooks/use-settings'
import { useUserUiPreferences } from '@/hooks/use-user-ui-preferences'
import { revealThemeChange } from '@/lib/themeTransition'
import { toTheme } from '@/lib/userUiPreferences'
import type { ThemePalettePreference } from '@/types/settings'
import { densityPx } from '@/theme/base/density'

/** Sélecteur thème à 3 positions : clair | auto | sombre (aligné sur le popover compte). */
export const THEME_TOGGLE_TRACK = {
  w: densityPx(72),
  h: densityPx(26),
  thumb: densityPx(20),
  pad: densityPx(3),
} as const

const THEME_ICON_TOOLTIP = {
  placement: 'top' as const,
  enterDelay: 400,
}

const thumbIconSx = {
  fontSize: densityPx(12),
  color: 'grey.800',
  display: 'block',
} as const

export function themePreferenceFromStored(
  stored: ThemePalettePreference | undefined
): ThemePalettePreference {
  return stored ?? 'light'
}

/** Barre visuelle + tooltips (identique au popover compte). Le clic est géré par le parent. */
export const ThemePaletteVisualTrack: FC = () => {
  const { t: tCommon } = useTranslate('common')
  const { paletteMode } = useSettings()
  const preference = themePreferenceFromStored(paletteMode)

  const themeModeLabel = useMemo(() => {
    if (preference === 'dark') return tCommon('darkMode')
    if (preference === 'auto') return tCommon('autoMode')
    return tCommon('lightMode')
  }, [preference, tCommon])

  const thumbLeft = useMemo(() => {
    const { w, thumb, pad } = THEME_TOGGLE_TRACK
    if (preference === 'dark') return w - thumb - pad
    if (preference === 'auto') return (w - thumb) / 2
    return pad
  }, [preference])

  const sunOpacity = preference === 'light' ? 0.95 : 0.35
  const autoOpacity = preference === 'auto' ? 0.95 : 0.35
  const moonOpacity = preference === 'dark' ? 0.95 : 0.35

  const thumbIcon =
    preference === 'dark' ? (
      <ModeNightIcon sx={thumbIconSx} aria-hidden />
    ) : preference === 'auto' ? (
      <BrightnessAuto sx={thumbIconSx} aria-hidden />
    ) : (
      <LightModeIcon sx={thumbIconSx} aria-hidden />
    )

  return (
    <Box
      aria-hidden
      sx={{
        position: 'relative',
        width: THEME_TOGGLE_TRACK.w,
        height: THEME_TOGGLE_TRACK.h,
        flexShrink: 0,
        ml: 'auto',
        borderRadius: THEME_TOGGLE_TRACK.h / 2,
        bgcolor: 'grey.700',
      }}
    >
      <Tooltip title={tCommon('lightMode')} {...THEME_ICON_TOOLTIP}>
        <Box
          component="span"
          sx={{
            position: 'absolute',
            left: 2,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: densityPx(22),
            height: THEME_TOGGLE_TRACK.h,
            pointerEvents: 'auto',
            cursor: 'inherit',
          }}
        >
          <LightModeIcon
            sx={{
              fontSize: densityPx(14),
              color: 'common.white',
              opacity: sunOpacity,
              pointerEvents: 'none',
            }}
          />
        </Box>
      </Tooltip>
      <Tooltip title={tCommon('autoMode')} {...THEME_ICON_TOOLTIP}>
        <Box
          component="span"
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: densityPx(22),
            height: THEME_TOGGLE_TRACK.h,
            pointerEvents: 'auto',
            cursor: 'inherit',
          }}
        >
          <BrightnessAuto
            sx={{
              fontSize: densityPx(14),
              color: 'common.white',
              opacity: autoOpacity,
              pointerEvents: 'none',
            }}
          />
        </Box>
      </Tooltip>
      <Tooltip title={tCommon('darkMode')} {...THEME_ICON_TOOLTIP}>
        <Box
          component="span"
          sx={{
            position: 'absolute',
            right: 2,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: densityPx(22),
            height: THEME_TOGGLE_TRACK.h,
            pointerEvents: 'auto',
            cursor: 'inherit',
          }}
        >
          <ModeNightIcon
            sx={{
              fontSize: densityPx(14),
              color: 'common.white',
              opacity: moonOpacity,
              pointerEvents: 'none',
            }}
          />
        </Box>
      </Tooltip>
      <Tooltip title={themeModeLabel} {...THEME_ICON_TOOLTIP}>
        <Box
          component="span"
          sx={(theme) => ({
            position: 'absolute',
            top: THEME_TOGGLE_TRACK.pad,
            left: thumbLeft,
            width: THEME_TOGGLE_TRACK.thumb,
            height: THEME_TOGGLE_TRACK.thumb,
            borderRadius: '50%',
            bgcolor: 'common.white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
            zIndex: 1,
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'inherit',
            transition: theme.transitions.create('left', {
              duration: theme.transitions.duration.shortest,
            }),
          })}
        >
          {thumbIcon}
        </Box>
      </Tooltip>
    </Box>
  )
}

export function useThemePaletteCycle() {
  const { t: tCommon } = useTranslate('common')
  const { paletteMode, handleUpdate } = useSettings()
  const { update } = useUserUiPreferences()
  const preference = themePreferenceFromStored(paletteMode)

  const themeModeLabel = useMemo(() => {
    if (preference === 'dark') return tCommon('darkMode')
    if (preference === 'auto') return tCommon('autoMode')
    return tCommon('lightMode')
  }, [preference, tCommon])

  const themeAriaLabel = `${tCommon('theme')}: ${themeModeLabel}. ${tCommon('themePressToCycle')}`

  const cycleTheme = useCallback(() => {
    const next: ThemePalettePreference =
      preference === 'light' ? 'auto' : preference === 'auto' ? 'dark' : 'light'
    void revealThemeChange(() => {
      handleUpdate({ paletteMode: next })
      void update({ theme: toTheme(next) })
    })
  }, [handleUpdate, preference, update])

  return { preference, themeModeLabel, themeAriaLabel, cycleTheme }
}
